import {
    Autocomplete,
    Chip,
    FormControl,
    FormHelperText,
    FormLabel,
    TextField,
  } from '@mui/material';
  import HelpTooltip from '../HelpTooltip';
  import { BiHelpCircle } from 'react-icons/bi';
  import { useEffect, useMemo, useState } from 'react';
  import { Close } from '@mui/icons-material';
  import { FbeOption } from '../../services/fbeTypes';
import FieldLock from '../FieldLock';

  export interface MultiSelectFreeProps {
    labelText: string;
    labelId: string;
    mandatory: boolean;
    helpTextTitle: string;
    helpTextBody: string;
    onChange: (value: string[]) => void;
    options: FbeOption[];
    selectedValues: FbeOption[];
    disabled: boolean;
    fullWidth: boolean;
    error?: boolean;
    helperText?: string;

    onLock: (field: string, locked: boolean) => void;
    disabledLock: boolean;
    displayedLock: boolean;
    isLocked: boolean;
  }

  const MultiSelectFree = (props: MultiSelectFreeProps): JSX.Element => {
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    useEffect(() => {
      if (props.selectedValues.length > 0) {
        setSelectedValues(props.selectedValues.map((opt) => opt.value));
      }
    }, [props.selectedValues]);

    const allOptions: FbeOption[] = useMemo(() => {
      const customOptions = selectedValues
        .filter((val) => !props.options.some((opt) => opt.value === val))
        .map((val) => ({ value: val, label: val, key: `key${val}`, disabled: false }));
      return [...props.options, ...customOptions];
    }, [props.options, selectedValues]);

    const handleChange = (_: any, value: (string | FbeOption)[]) => {
      const values = value.map((v) =>
        typeof v === 'string' ? v : v.value
      );
      setSelectedValues(values);
      props.onChange(values);
    };

    return (
      <FormControl fullWidth={props.fullWidth} error={props.error || false}>
        <FormLabel
          component="legend"
          sx={{ fontSize: '12px', mb: 1.5 }}
          htmlFor={props.labelId}
        >
        <FieldLock
            onChange={function (locked: boolean): void {
                props.onLock(props.labelId,locked);
            } }
            labelId={props.labelId}
            disabled={props.disabledLock}
            displayed={props.displayedLock}
            value={props.isLocked}/>
          {props.labelText} <b>{props.mandatory ? '*' : ''}</b>{' '}
          <HelpTooltip title={props.helpTextTitle} body={props.helpTextBody}> <BiHelpCircle /> </HelpTooltip>
        </FormLabel>

        <Autocomplete
          data-testid={props.labelId}
          multiple
          freeSolo
//          disableCloseOnSelect
          options={allOptions}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.label
          }
          filterSelectedOptions
          value={selectedValues.map((val) => {
            const found = allOptions.find((o) => o.value === val);
            return found ?? val;
          })}
          onChange={handleChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const label = typeof option === 'string' ? option : option.label;
              return (
                <Chip
                  label={label}
                  {...getTagProps({ index })}
                  deleteIcon={
                    <Close
                      sx={{
                        color: 'white',
                        fontSize: '14px',
                        borderRadius: '16px',
                        bgcolor: 'lightgray',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                        },
                      }}
                    />
                  }
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              id={props.labelId}
              name={props.labelId}
              variant="outlined"
              sx={{ bgcolor: '#FFFFFF' }}
              disabled={props.disabled}
              autoComplete="off"
              inputProps={{
                ...params.inputProps,
                'aria-label': props.labelText
              }}
            />
          )}
          disabled={props.disabled}
        />

        {props.helperText && (
          <FormHelperText>{props.helperText}</FormHelperText>
        )}
      </FormControl>
    );
  };

  export default MultiSelectFree;